import { useEffect, useState } from "react";
import instance from "../../../axios/instance";
import {
  CardAddressInput,
  CardCVVInput,
  CardExpiryInput,
  CardNameInput,
  CardNumberInput,
  CardzipInput,
} from "../../inputs/CreditCardForm";
import { CreditCard, Hash, X } from "lucide-react";
import Spinner from "../../spinners/Spinner";
import {
  detectCardType,
  formatCardNumber,
  formatExpiryDate,
  validateCardNumber,
  validateCVV,
  validateExpiryDate,
} from "../../../utils/cardUtils";
import { getToken } from "../../../utils/token";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { Calendar } from "iconsax-react";

export default function PaymentCardInfoModal({
  address,
  zipcode,
  orderId,
  facilityId,
  open,
  onClose,
  triggerRevalidate,
  cards: initialCards,
}) {
  const [loading, setLoading] = useState(false);
  const [cardsData, setCardsData] = useState(initialCards || null);
  const [selectedCardType, setSelectedCardType] = useState("custom");
  const [saveCard, setSaveCard] = useState(false);
  const [saveCardType, setSaveCardType] = useState("personal"); // "personal" | "clinic"

  const [cardData, setCardData] = useState({
    number: "",
    expiry: "",
    cvv: "",
    address: address || "",
    zip: zipcode || "",
    name: "",
  });
  const [errors, setErrors] = useState({});
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  const token = getToken();

  useEffect(() => {
    let mounted = true;
    (async () => {
      mounted && setInvoiceLoading(true);
      try {
        const invoice = await instance.get(
          `/doctor/reps/orders/payment-invoice?id=${orderId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (mounted) {
          setInvoiceInfo(invoice.data);
          setInvoiceLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted) setInvoiceLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [orderId, token]);

  useEffect(() => {
    if (initialCards) {
      setCardsData(initialCards);
    } else {
      let mounted = true;
      (async () => {
        try {
          const res = await instance.get("/doctor/cards", {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (mounted && res.data) {
            setCardsData(res.data);
          }
        } catch (err) {
          console.error("Error fetching cards:", err);
        }
      })();
      return () => {
        mounted = false;
      };
    }
  }, [initialCards, token]);

  const applyCardSelection = (typeKey, cardsObj) => {
    setSelectedCardType(typeKey);
    if (typeKey === "doctor" && cardsObj?.doctor) {
      const c = cardsObj.doctor;
      setCardData({
        number: c.card_number || "",
        expiry: c.card_expiry || "",
        cvv: c.card_cvv || "",
        address: c.card_address || "",
        zip: c.card_zip || "",
        name: c.card_name || "",
      });
      setErrors({});
    } else if (typeKey === "facility" && cardsObj?.facility) {
      const c = cardsObj.facility;
      setCardData({
        number: c.card_number || "",
        expiry: c.card_expiry || "",
        cvv: c.card_cvv || "",
        address: c.card_address || "",
        zip: c.card_zip || "",
        name: c.card_name || "",
      });
      setErrors({});
    } else if (typeKey === "custom") {
      setCardData({
        number: "",
        expiry: "",
        cvv: "",
        address: address || "",
        zip: zipcode || "",
        name: "",
      });
      setErrors({});
    }
  };

  useEffect(() => {
    if (cardsData) {
      if (cardsData.doctor) {
        applyCardSelection("doctor", cardsData);
      } else if (cardsData.facility) {
        applyCardSelection("facility", cardsData);
      }
    }
  }, [cardsData]);

  const btnDisabled =
    !cardData.address ||
    !cardData.zip ||
    !cardData.cvv ||
    !cardData.expiry ||
    !cardData.number;

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    Object.keys(cardData).forEach((key) => {
      const field = key;
      const error = validateField(field, cardData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    setLoading(true);

    if (Object.keys(newErrors).length === 0) {
      try {
        await instance.post(
          "doctor/reps/orders/pay-invoice",
          { orderId, cardInfo: cardData, saveCard, saveCardType },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (saveCard) {
          try {
            const saveEndpoint =
              saveCardType === "clinic"
                ? "/doctor/facility/card/update"
                : "/doctor/card/update";

            await instance.put(
              saveEndpoint,
              {
                number: cardData.number,
                expiry: cardData.expiry,
                cvv: cardData.cvv,
                address: cardData.address,
                zip: cardData.zip,
                name: cardData.name || "",
                ...(saveCardType === "clinic" ? { id: facilityId } : {}),
              },
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );
          } catch (saveError) {
            console.error("Error saving card to account:", saveError);
          }
        }

        setLoading(false);
        toast.success("Payment successful");
        if (triggerRevalidate) triggerRevalidate();
        setCardData({
          number: "",
          cvv: "",
          expiry: "",
          address: "",
          zip: "",
          name: "",
        });
        onClose();
      } catch (error) {
        console.error(error.response?.data);
        toast.error(error.response?.data?.message || "An error occurred");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const cardType = detectCardType(cardData.number);
  const groupedOrders = Array.isArray(invoiceInfo?.groupedOrders)
    ? invoiceInfo.groupedOrders
    : [];
  const showGroupedOrders = groupedOrders.length > 1;
  const groupedSummary = invoiceInfo?.groupedSummary || null;
  const showPatientShipping =
    groupedSummary?.deliveryType === "deliver to patient";

  const validateField = (field, value) => {
    switch (field) {
      case "number":
        return validateCardNumber(value) ? "" : "Invalid card number";
      case "expiry":
        return validateExpiryDate(value) ? "" : "Invalid expiry date";
      case "cvv":
        return validateCVV(value, cardType) ? "" : "Invalid CVV";
      case "address":
        return value.trim().length >= 2
          ? ""
          : "Address must be at least 2 characters";
      case "zip":
        return value.trim().length >= 3
          ? ""
          : "Zip code must be at least 3 characters";
      default:
        return "";
    }
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    if (field === "number") {
      formattedValue = formatCardNumber(value);
    } else if (field === "expiry") {
      formattedValue = formatExpiryDate(value);
    } else if (field === "cvv") {
      formattedValue = value
        .replace(/\D/g, "")
        .slice(0, cardType === "amex" ? 4 : 3);
    } else if (field === "name") {
      formattedValue = value.replace(/[^a-zA-Z\s]/g, "").slice(0, 50);
    }

    setCardData((prev) => ({ ...prev, [field]: formattedValue }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleBlur = (field) => {
    const error = validateField(field, cardData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const hasSavedCards = cardsData && (cardsData.doctor || cardsData.facility);

  return (
    <div
      className={`${
        open ? "flex" : "hidden"
      } justify-center items-center fixed top-0 z-[1000] bottom-0 right-0 left-0 bg-[#11111180]`}
    >
      <div className="bg-white rounded-xl max-w-[850px] w-full h-full lg:h-[80vh] flex flex-col overflow-hidden">
        <div className="p-4 flex shrink-0 bg-white rounded-t-xl justify-between items-center border-b">
          <div className="font-medium text-[15px]">Pay invoice</div>
          <button onClick={onClose}>
            <X size={22} />
          </button>
        </div>

        <div className="grid grid-cols-1 w-full flex-1 min-h-0 overflow-hidden lg:grid-cols-2">
          <div className="px-4 py-3 border-r lg:pb-10 min-h-0 overflow-y-auto scrollbar-hide">
            <h2 className="flex items-center gap-x-3 text-xl font-medium">
              Invoice Info {invoiceLoading && <Spinner />}
            </h2>
            {invoiceInfo && (
              <div className="mt-2">
                <span className="flex gap-x-1 items-center text-sm font-normal">
                  <Hash size={14} />
                  {invoiceInfo.receiptNumber}
                </span>
                <span className="flex gap-x-1 items-center text-sm font-normal my-2">
                  <Calendar size={14} />
                  {dayjs(invoiceInfo.invoiceDate).format(
                    "ddd, MMM DD YYYY [at] h:mm A",
                  )}
                </span>

                <h2 className="mt-5 font-medium">
                  {showGroupedOrders ? "Patients / Ordered Items" : "Items"}
                </h2>
                {showGroupedOrders && (
                  <div className="space-y-3 my-3">
                    {groupedOrders.map((groupOrder) => (
                      <div
                        key={groupOrder.orderId}
                        className="border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                          <p className="text-sm font-medium">
                            {groupOrder.patientName}
                          </p>
                          <p className="text-xs text-gray-500">
                            Order #{groupOrder.orderId}
                          </p>
                        </div>
                        <div className="divide-y divide-gray-100">
                          {groupOrder.medications.map((medication, index) => (
                            <div
                              key={`${groupOrder.orderId}-${medication.name}-${index}`}
                              className="px-3 py-2 flex justify-between gap-x-3"
                            >
                              <div>
                                <p className="text-sm">{medication.name}</p>
                                {(medication.daysOfSupply ||
                                  medication.strength ||
                                  medication.size) && (
                                  <p className="text-xs text-gray-500">
                                    {[
                                      medication.daysOfSupply
                                        ? `${medication.daysOfSupply} days`
                                        : "",
                                      medication.strength || "",
                                      medication.size || "",
                                    ]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </p>
                                )}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-sm font-medium">
                                  $
                                  {Number(
                                    medication.amount || 0,
                                  ).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Qty: {medication.quantity || 1}
                                </p>
                              </div>
                            </div>
                          ))}
                          {showPatientShipping && (
                            <div className="px-3 py-2 flex justify-between gap-x-3 bg-blue-50/50">
                              <div>
                                <p className="text-sm">Shipping</p>
                                <p className="text-xs text-gray-500">
                                  {groupOrder.shippingService || "-/-"}
                                </p>
                              </div>
                              <p className="text-sm font-medium text-right shrink-0">
                                $
                                {Number(
                                  groupOrder.shippingAmount || 0,
                                ).toLocaleString()}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {showGroupedOrders && (
                  <h2 className="mt-5 font-medium">Invoice Charges</h2>
                )}
                <div className="space-y-3 my-3">
                  {invoiceInfo.purchases.map((purchase) => (
                    <div
                      key={purchase.id}
                      className="border border-gray-200 px-3 py-3 rounded-lg flex justify-between items-center gap-x-3"
                    >
                      <p className="text-sm">{purchase.productName}</p>
                      <div>
                        <p className="text-sm font-medium mb-1 text-right">
                          ${Number(purchase.totalAmount).toLocaleString()}
                        </p>
                        <p className="text-xs text-right">
                          Qty: {purchase.quantity}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="-mx-4 px-4 border-t border-gray-200 my-5 pt-3 space-y-2">
                  {showGroupedOrders && groupedSummary && (
                    <>
                      <div className="flex gap-x-3 items-center justify-between text-sm">
                        <p>Items Total</p>
                        <p className="font-medium text-right">
                          ${Number(groupedSummary.itemTotal || 0).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-x-3 items-center justify-between text-sm">
                        <p>Shipping Total</p>
                        <p className="font-medium text-right">
                          $
                          {Number(
                            groupedSummary.shippingTotal || 0,
                          ).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                  <div className="flex gap-x-3 items-center justify-between">
                    <p>Total</p>
                    <p className="font-medium mb-1 text-right">
                      $
                      {Number(
                        groupedSummary?.finalTotal || invoiceInfo.totalAmount,
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <form
            onSubmit={handleFormSubmit}
            className="pt-3 pb-10 px-4 space-y-5"
          >
            {hasSavedCards && (
              <div>
                <label
                  className="block font-semibold uppercase text-[12px] text-gray-600 mb-2"
                  htmlFor="savedCardSelect"
                >
                  Select Saved Payment Card
                </label>
                <select
                  id="savedCardSelect"
                  value={selectedCardType}
                  onChange={(e) =>
                    applyCardSelection(e.target.value, cardsData)
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm bg-white font-mono focus:border-primary outline-none transition-all cursor-pointer"
                >
                  {cardsData.doctor && (
                    <option value="doctor">
                      Doctor Card (
                      {cardsData.doctor.card_name
                        ? `${cardsData.doctor.card_name} - `
                        : ""}
                      ****{" "}
                      {cardsData.doctor.card_number
                        ? cardsData.doctor.card_number.slice(-4)
                        : "****"}
                      )
                    </option>
                  )}
                  {cardsData.facility && (
                    <option value="facility">
                      Facility Card (
                      {cardsData.facility.card_name
                        ? `${cardsData.facility.card_name} - `
                        : ""}
                      ****{" "}
                      {cardsData.facility.card_number
                        ? cardsData.facility.card_number.slice(-4)
                        : "****"}
                      )
                    </option>
                  )}
                  <option value="custom">Use another card</option>
                </select>
              </div>
            )}
            <CardNumberInput
              handleBlur={handleBlur}
              handleInputChange={handleInputChange}
              cardData={cardData}
              errors={errors}
              cardType={cardType}
            />
            <div className="grid grid-cols-2 gap-x-4">
              <CardExpiryInput
                handleBlur={handleBlur}
                handleInputChange={handleInputChange}
                cardData={cardData}
                errors={errors}
              />
              <CardCVVInput
                handleBlur={handleBlur}
                handleInputChange={handleInputChange}
                cardData={cardData}
                errors={errors}
                cardType={cardType}
              />
            </div>
            <div className="grid grid-cols-[auto_30%] gap-x-4">
              <CardAddressInput
                handleBlur={handleBlur}
                handleInputChange={handleInputChange}
                cardData={cardData}
                errors={errors}
              />
              <CardzipInput
                handleBlur={handleBlur}
                handleInputChange={handleInputChange}
                cardData={cardData}
                errors={errors}
              />
            </div>

            {/* Save Card Checklist */}
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <label className="flex items-center gap-2.5 cursor-pointer text-sm font-medium text-gray-700 select-none">
                <input
                  type="checkbox"
                  checked={saveCard}
                  onChange={(e) => setSaveCard(e.target.checked)}
                  className="w-4 h-4 text-primary accent-primary rounded border-gray-300 focus:ring-primary cursor-pointer"
                />
                <span>Save card details in Blendcare</span>
              </label>

              {saveCard && (
                <div className="pl-6 space-y-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Save card as:
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none">
                      <input
                        type="radio"
                        name="saveCardType"
                        value="personal"
                        checked={saveCardType === "personal"}
                        onChange={() => setSaveCardType("personal")}
                        className="w-4 h-4 text-primary accent-primary cursor-pointer"
                      />
                      <span>Personal Card</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none">
                      <input
                        type="radio"
                        name="saveCardType"
                        value="clinic"
                        checked={saveCardType === "clinic"}
                        onChange={() => setSaveCardType("clinic")}
                        className="w-4 h-4 text-primary accent-primary cursor-pointer"
                      />
                      <span>Clinic Card</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="col-span-2 flex">
              <button
                disabled={btnDisabled || loading}
                type="submit"
                className={`flex gap-x-2 items-center justify-center text-white bg-primary p-3 font-medium text-[14px] rounded-md disabled:opacity-60 disabled:cursor-not-allowed shadow-sm w-full`}
              >
                {loading ? <Spinner color="white" /> : <CreditCard size={18} />}{" "}
                Pay now
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <svg
                  className="w-2 h-2 text-white"
                  fill="currentColor"
                  viewBox="0 0 8 8"
                >
                  <path d="M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z" />
                </svg>
              </div>
              Your card is safely encrypted when saved
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
